import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, Link2, Loader } from 'lucide-react';
import GescomProjectLinkModal from './GescomProjectLinkModal.jsx';
import { apiGescomExport } from '../api/client.js';

/**
 * Champ « N° projet Gescom » de la fiche affaire (`Affaire.gescomProjectId`).
 *
 * - la saisie libre reste possible ;
 * - sous le champ, le projet est résolu dans le catalogue Gescom : libellé si
 *   le numéro existe, avertissement s'il est inconnu (le push échouerait) ;
 * - « Lier à Gescom » ouvre la même modale que l'Export Gescom (suggestions
 *   par code CH + recherche), en mode choix : le numéro est posé dans le
 *   formulaire et enregistré avec le reste de la fiche.
 */
export default function GescomProjectField({ value, onChange, affaireId, affaireName, inputStyle }) {
  const [lookup, setLookup] = useState({ state: 'idle' }); // idle | loading | found | unknown | error
  const [pickerOpen, setPickerOpen] = useState(false);
  const trimmed = (value ?? '').trim();

  useEffect(() => {
    if (!trimmed) { setLookup({ state: 'idle' }); return undefined; }
    if (!/^\d{1,10}$/.test(trimmed)) {
      setLookup({ state: 'unknown', message: 'Un numéro de projet Gescom est uniquement composé de chiffres.' });
      return undefined;
    }
    let cancelled = false;
    setLookup({ state: 'loading' });
    const t = setTimeout(() => {
      apiGescomExport.catalog({ projectId: trimmed })
        .then((r) => {
          if (cancelled) return;
          setLookup(r.current
            ? { state: 'found', project: r.current }
            : { state: 'unknown', message: `Le projet ${trimmed} n’existe pas dans Gescom : le push de ses saisies échouera.` });
        })
        .catch((e) => { if (!cancelled) setLookup({ state: 'error', message: e.message || 'Catalogue Gescom indisponible' }); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [trimmed]);

  const others = lookup.state === 'found'
    ? (lookup.project.linkedAffaires ?? []).filter((a) => a.id !== affaireId)
    : [];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={128}
          placeholder="—"
          style={{ ...inputStyle, flex: 1 }}
        />
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          title="Choisir le projet dans le catalogue Gescom"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px',
            borderRadius: 'var(--radius-sm)', border: '1px solid var(--accent)',
            background: 'transparent', color: 'var(--accent)', fontSize: 13,
            cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
          }}
        >
          <Link2 size={14} /> Lier à Gescom
        </button>
      </div>

      <div style={{ marginTop: 6, fontSize: 12.5, minHeight: 18 }}>
        {lookup.state === 'loading' ? (
          <span style={{ color: 'var(--text-muted)', display: 'inline-flex', gap: 5, alignItems: 'center' }}>
            <Loader size={13} className="spin" /> Vérification dans Gescom…
          </span>
        ) : lookup.state === 'found' ? (
          <>
            <span style={{ color: 'var(--text)', display: 'inline-flex', gap: 5, alignItems: 'center' }}>
              <CheckCircle size={13} style={{ color: 'var(--success)', flexShrink: 0 }} />
              {lookup.project.libelle}
              {!lookup.project.isActive ? <span style={{ color: 'var(--warning)' }}>· inactif dans Gescom</span> : null}
            </span>
            {lookup.project.codeAff || lookup.project.codeProjet ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 11.5, marginLeft: 18 }}>
                {lookup.project.codeAff ? `aff. ${lookup.project.codeAff}` : ''}
                {lookup.project.codeAff && lookup.project.codeProjet ? ' · ' : ''}
                {lookup.project.codeProjet ? `projet ${lookup.project.codeProjet}` : ''}
              </div>
            ) : null}
            {others.length > 0 ? (
              <div style={{ color: 'var(--warning)', fontSize: 11.5, marginLeft: 18 }}>
                Aussi lié à : {others.map((a) => a.name).join(', ')}
              </div>
            ) : null}
          </>
        ) : lookup.state === 'unknown' || lookup.state === 'error' ? (
          <span style={{ color: lookup.state === 'unknown' ? 'var(--warning)' : 'var(--text-muted)', display: 'inline-flex', gap: 5, alignItems: 'center' }}>
            <AlertCircle size={13} style={{ flexShrink: 0 }} /> {lookup.message}
          </span>
        ) : null}
      </div>

      {pickerOpen ? (
        <GescomProjectLinkModal
          affaire={{ id: affaireId, name: affaireName || 'Nouvelle affaire', gescomProjectId: trimmed || null }}
          onClose={() => setPickerOpen(false)}
          onPick={(p) => { onChange(p.gescomProjectId); setPickerOpen(false); }}
        />
      ) : null}
    </div>
  );
}
